import { useConfigStore } from '@xantory/store';
import { ApiError } from './client';
import type { WsKind } from '../domains/iot/iot.types';

/**
 * Map a logical endpoint path to the final URL for the current deployment mode. Together with the
 * per-domain `*.endpoints.ts` files this is the ONLY place that knows how a path maps to a tier (D-040).
 *
 *   Local: used as-is — gateway-local serves /auth and /v1.
 *   Cloud:
 *     /users/*        → unchanged (admin, invitations, sites — already cloud-shaped)
 *     /auth/*         → /users/auth/*        (login door at gateway-cloud)
 *     /v1/user[/...]  → /users/v1/user[/...] (self profile/password at gateway-cloud)
 *     other /v1/*     → /sites/{siteId}/v1/* (IoT at the selected site's gateway-local)
 */
export function resolvePath(path: string): string {
  const { mode, siteId } = useConfigStore.getState();
  if (mode === 'local') return path;

  if (path.startsWith('/users/')) return path;
  if (path.startsWith('/auth/')) return '/users' + path;
  if (path === '/v1/user' || path.startsWith('/v1/user/')) return '/users' + path;
  // Site-scoped surfaces (IoT REST + the live WebSockets) route through the selected site's
  // reverse-proxy hop: proxy-cloud strips `/sites/{id}` and forwards to `proxy-local`, which
  // routes `/ws/*`→iot-live and `/v1/*`→gateway-local. Cloud mode has exactly one selected site.
  if (path.startsWith('/v1/') || path.startsWith('/ws/')) {
    if (!siteId) {
      throw new ApiError(400, 'NO_SITE_SELECTED', 'No site selected for this cloud-mode request');
    }
    return `/sites/${siteId}${path}`;
  }
  return path;
}

/**
 * Build the full, same-origin ws(s):// URL for a location — mode-aware via `resolvePath` (D-040).
 * `kind` is the subscription group (`grow_room` | `plumbing_room`); a grow-room stream also
 * carries its racks' events (D-049).
 * Local: `/ws/{kind}/{code}`; cloud: `/sites/{id}/ws/{kind}/{code}`.
 */
export function resolveWsUrl(kind: WsKind, code: string): string {
  const scheme = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  return `${scheme}//${window.location.host}${resolvePath(`/ws/${kind}/${encodeURIComponent(code)}`)}`;
}

/**
 * Build the full ws(s):// URL for the global device-liveness stream — mode-aware via
 * `resolvePath` (D-040). Not location-scoped: one socket carries every device's health.
 * Local: `/ws/health`.
 */
export function resolveWsHealthUrl(): string {
  const scheme = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  return `${scheme}//${window.location.host}${resolvePath('/ws/health')}`;
}

/**
 * The cloud fleet-status socket (D-137).
 *
 * Deliberately NOT built through `resolvePath`: that rewrites `/v1/*` to
 * `/sites/{siteId}/v1/*` for site-scoped calls, and this stream is fleet-wide —
 * routing it at a single site would ask one farm about every farm. It is a cloud
 * endpoint, so it takes the console's own origin as-is.
 *
 * The caller appends its single-use `?ticket=` — see `useFleetLive`.
 */
export function resolveFleetWsUrl(): string {
  const scheme = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  return `${scheme}//${window.location.host}/users/v1/fleet/ws`;
}
